import type {FilterProvider} from '@github-ui/filter'
import type {QuerySegment} from '@github-ui/filter-query'
import {validateQueryKeys} from '@github-ui/filter-query'
import {human} from '@github-ui/formatters'
import {ActionList, Heading} from '@primer/react'
import {AriaStatus} from '@primer/react/experimental'
import {useState} from 'react'

import {ListItemRepoIcon} from './components/ListItemRepoIcon'
import {getBlankMessage, getResultsAnnouncement, ListMessage} from './components/ListMessage'
import {PartialResultsRow} from './components/PartialResultsRow'
import {PickerDialog} from './components/PickerDialog'
import {getRepoName} from './components/PickerList'
import {ReposPickerHeaderContent} from './components/ReposPickerHeaderContent'
import {useQueryRepositories} from './hooks/use-query-repositories'
import type {CommonDialogProps, DynamicMatchingProps} from './types'

type ReposPickerDialogProps = DynamicMatchingProps & CommonDialogProps

const noFilterLiterals = {
  title: 'No filter added',
  description: 'Add a filter using the input to match repositories.',
}

export function DynamicReposPickerDialog({
  scope,
  query: initialQuery = '',
  onSubmit,
  onDismiss,
  returnFocusRef,
  providers,
  warnIfUnsupportedProvider,
}: ReposPickerDialogProps) {
  const {executedQuery, currentQuery, setCurrentQuery, setExecutedQuery, validationMessages} = useQuery({
    initialQuery,
    providers,
    warnIfUnsupportedProvider,
  })
  const results = useQueryRepositories({scope, query: executedQuery, enabled: !!executedQuery})

  const items = executedQuery ? results.data?.repositories || [] : []
  const matchingItemsCount = executedQuery ? results.data?.repositoryCount || 0 : 0

  const blankMessage = getBlankMessage(results, items)
  const blankState = executedQuery ? blankMessage && <ListMessage>{blankMessage}</ListMessage> : <NoFilterMessage />

  const messageToAnnounce = getDynamicResultsAnnouncement({
    executedQuery,
    blankMessage,
    visibleItemsCount: items.length,
    matchingItemsCount,
  })

  const onApply = () => {
    onSubmit(currentQuery)
    onDismiss()
  }

  return (
    <PickerDialog
      onClose={onDismiss}
      footerButtons={[
        {
          onClick: onDismiss,
          content: 'Cancel',
        },
        {
          buttonType: 'primary',
          onClick: onApply,
          content: 'Apply',
        },
      ]}
      onSubmit={onApply}
      returnFocusRef={returnFocusRef}
      renderHeader={({initialFocusRef, dialogLabelId, dialogDescriptionId}) => (
        <>
          <ReposPickerHeaderContent
            dialogTitle="Filter"
            dialogLabelId={dialogLabelId}
            dialogDescription="Use a query to select repositories"
            dialogDescriptionId={dialogDescriptionId}
            inputRef={initialFocusRef}
            initialQuery={initialQuery}
            scope={scope}
            providers={providers}
            onDismiss={onDismiss}
            onQueryExecuted={setExecutedQuery}
            onQueryChange={setCurrentQuery}
            extraValidationMessages={validationMessages}
          />
          <ItemMatchCounter matchingItemsCount={matchingItemsCount} />
          <AriaStatus announceOnShow className="sr-only">
            {messageToAnnounce}
          </AriaStatus>
        </>
      )}
      renderBody={() => (
        <PickerDialog.Body>
          {blankState || (
            <ActionList role="list" aria-label="Repository List">
              {items.map(repo => (
                <ActionList.Item key={repo.id} role="listitem">
                  <ActionList.LeadingVisual>
                    <ListItemRepoIcon visibility={repo.visibility} />
                  </ActionList.LeadingVisual>
                  {getRepoName(repo, scope.type)}
                </ActionList.Item>
              ))}
            </ActionList>
          )}
          <PartialResultsRow itemCount={items.length} totalCount={matchingItemsCount} />
        </PickerDialog.Body>
      )}
    />
  )
}

interface UseQueryProps {
  initialQuery: string
  providers?: FilterProvider[]
  warnIfUnsupportedProvider?: boolean
}
function useQuery({initialQuery, providers, warnIfUnsupportedProvider}: UseQueryProps) {
  const [currentQuery, setCurrentQuery] = useState(initialQuery)
  const [executedQuery, setExecutedQuery] = useState(initialQuery)

  const [validationMessages, setValidationMessages] = useState<string[]>(() => {
    if (!warnIfUnsupportedProvider) return []
    return getFiltersInvalidMessage(validateQueryKeys(initialQuery, providers))
  })

  const onQueryExecuted = (newQuery: string) => {
    if (warnIfUnsupportedProvider && newQuery !== executedQuery) {
      setValidationMessages(getFiltersInvalidMessage(validateQueryKeys(newQuery, providers)))
    }

    setExecutedQuery(newQuery)
  }

  return {currentQuery, executedQuery, setCurrentQuery, setExecutedQuery: onQueryExecuted, validationMessages}
}

function ItemMatchCounter({matchingItemsCount}: {matchingItemsCount: number}) {
  return (
    <div className="px-3 py-2 color-fg-muted text-small color-bg-subtle border-bottom text-semibold">
      {human(matchingItemsCount)} {matchingItemsCount === 1 ? 'repository' : 'repositories'} matching
    </div>
  )
}

function NoFilterMessage() {
  return (
    <ListMessage>
      <Heading as="h3" variant="small">
        {noFilterLiterals.title}
      </Heading>
      <p className="color-fg-muted">{noFilterLiterals.description}</p>
    </ListMessage>
  )
}

export function getFiltersInvalidMessage(invalidSegments: QuerySegment[]) {
  const invalidQualifiers = invalidSegments
    .filter(segment => segment.type === 'filter')
    .map(segment => segment.key)
    .sort()
  const hasFreeText = invalidSegments.some(segments => segments.type === 'text')

  const result = hasFreeText ? ['Free text is not supported.'] : []

  // eslint-disable-next-line github/unescaped-html-literal -- ValidationMessage will escape this safely
  return result.concat(invalidQualifiers.map(key => `<pre>${key}</pre> is not supported.`))
}

export function getDynamicResultsAnnouncement({
  executedQuery,
  blankMessage,
  matchingItemsCount,
  visibleItemsCount,
}: {
  executedQuery: string
  blankMessage: string
  matchingItemsCount: number
  visibleItemsCount: number
}) {
  return executedQuery
    ? getResultsAnnouncement({blankMessage, totalItemsCount: matchingItemsCount, itemsCount: visibleItemsCount})
    : `${noFilterLiterals.title}. ${noFilterLiterals.description}`
}

try{ DynamicReposPickerDialog.displayName ||= 'DynamicReposPickerDialog' } catch {}
try{ ItemMatchCounter.displayName ||= 'ItemMatchCounter' } catch {}
try{ NoFilterMessage.displayName ||= 'NoFilterMessage' } catch {}